import { createFileRoute } from "@tanstack/react-router";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { checkBilinfoSyncHealth } from "@/lib/sync-health.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  AlertCircle,
  Activity,
  Lock,
  Wifi,
  RefreshCw,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/sync-health")({
  head: () => ({
    meta: [
      { title: "Bilinfo health check — AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (<AdminOnlyGuard><AdminSyncHealth /></AdminOnlyGuard>),
});

function AdminSyncHealth() {
  const runCheck = useServerFn(checkBilinfoSyncHealth);

  const checkMutation = useMutation({
    mutationFn: async () => runCheck({ data: undefined }),
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "Kunne ikke køre health-check");
    },
  });

  // Auto-run on mount.
  useEffect(() => {
    checkMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = checkMutation.data;
  const isLoading = checkMutation.isPending;

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bilinfo health-check</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Verificerer at sync-feedet svarer (HTTP 200/405), at auth-gaten virker, og viser status
            for seneste kørsel.
          </p>
        </div>
        <Button
          onClick={() => checkMutation.mutate()}
          disabled={isLoading}
          className="gap-2 bg-accent text-accent-foreground hover:brightness-110"
        >
          <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          {isLoading ? "Tjekker..." : "Kør check igen"}
        </Button>
      </div>

      {/* Overall status */}
      <div className="mt-8">
        {isLoading && !result ? (
          <div className="rounded-xl bg-card p-8 text-center text-sm text-muted-foreground ring-1 ring-border">
            Kører health-check mod{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
              https://autoxchange.dk/hooks/sync-bilinfo
            </code>
            ...
          </div>
        ) : result ? (
          <div
            className={`rounded-xl p-6 ring-1 ${
              result.overallHealthy
                ? "bg-emerald-50 ring-emerald-200 dark:bg-emerald-950/20 dark:ring-emerald-900"
                : "bg-destructive/5 ring-destructive/30"
            }`}
          >
            <div className="flex items-center gap-3">
              {result.overallHealthy ? (
                <CheckCircle2 size={28} className="text-emerald-600 dark:text-emerald-400" />
              ) : (
                <AlertCircle size={28} className="text-destructive" />
              )}
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {result.overallHealthy ? "Feed er sundt" : "Feed har problemer"}
                </h2>
                <p className="text-xs text-muted-foreground">
                  Tjekket {new Date(result.checkedAt).toLocaleString("da-DK")} ·{" "}
                  {(result.durationMs / 1000).toFixed(2)}s
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {result && (
        <>
          {/* Endpoint checks */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <CheckCard
              icon={<Wifi size={18} />}
              label="Endpoint reachable"
              ok={result.reachable}
              detail={
                result.reachError
                  ? `Fejl: ${result.reachError}`
                  : result.httpStatus != null
                    ? `HTTP ${result.httpStatus}${result.httpStatus === 405 ? " (Method Not Allowed — forventet for HEAD)" : ""}`
                    : "Ukendt status"
              }
            />
            <CheckCard
              icon={<Lock size={18} />}
              label="Auth-gate aktiv"
              ok={result.authGateOk}
              detail={
                result.authGateStatus != null
                  ? `Bad token → HTTP ${result.authGateStatus}${result.authGateStatus === 401 ? " (forventet)" : " (forventede 401)"}`
                  : "Ingen response"
              }
            />
          </div>

          {/* Target URL */}
          <div className="mt-4 rounded-xl bg-card p-4 text-sm ring-1 ring-border">
            <span className="text-muted-foreground">Target:</span>{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{result.targetUrl}</code>
          </div>

          {/* Last run */}
          <div className="mt-8 rounded-xl bg-card ring-1 ring-border">
            <div className="flex items-center justify-between gap-3 border-b border-border p-6">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Seneste kørsel</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Status og bil-tællinger fra det seneste sync-run.
                </p>
              </div>
              <Activity size={18} className="text-muted-foreground" />
            </div>
            {result.lastRun ? (
              <div className="space-y-4 p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <RunStatusBadge status={result.lastRun.status} />
                  <Badge variant="outline" className="gap-1">
                    Mode: {result.lastRun.mode}
                  </Badge>
                  <Badge variant="outline">{result.lastRun.triggeredBy}</Badge>
                  <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock size={12} />
                    {new Date(result.lastRun.createdAt).toLocaleString("da-DK")}
                    {result.lastRun.durationMs != null &&
                      ` · ${(result.lastRun.durationMs / 1000).toFixed(1)}s`}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <CountTile label="I feed" value={result.lastRun.carsInFeed} />
                  <CountTile label="Oprettet" value={result.lastRun.carsCreated} />
                  <CountTile label="Opdateret" value={result.lastRun.carsUpdated} />
                  <CountTile label="Markeret solgt" value={result.lastRun.carsMarkedSold} />
                </div>

                {result.lastRun.errorMessage && (
                  <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                    <p className="font-medium">Fejlbesked</p>
                    <p className="mt-1 text-xs">{result.lastRun.errorMessage}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Ingen sync-runs fundet endnu.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CheckCard({
  icon,
  label,
  ok,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  ok: boolean;
  detail: string;
}) {
  return (
    <div className="rounded-xl bg-card p-5 ring-1 ring-border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
        </div>
        {ok ? (
          <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
        ) : (
          <AlertCircle size={18} className="text-destructive" />
        )}
      </div>
      <p className="mt-3 text-sm text-foreground">{detail}</p>
    </div>
  );
}

function CountTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-muted/50 p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
    </div>
  );
}

function RunStatusBadge({ status }: { status: string }) {
  if (status === "success") {
    return (
      <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300">
        <CheckCircle2 size={12} /> Success
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertCircle size={12} /> Error
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="capitalize">
      {status}
    </Badge>
  );
}
